"use client";

import { cn } from "@scott/ui/utils/cn";
import { ChevronDown } from "lucide-react";
import { useState } from "react";
import { Body } from "../body";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../command";
import { CloseIcon } from "../icon/icons/close-icon";
import { TickIcon } from "../icon/icons/tick-icon";
import { Popover, PopoverContent, PopoverTrigger } from "../popover";

interface ModelOption {
  id: string;
  label: string;
}

interface SearchableSelectProps {
  options: ModelOption[];
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  status?: "error" | "success" | "none";
  message?: string;
  disabled?: boolean;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = "Select a product",
  status = "none",
  message,
  disabled = false,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const selectedOption = options.find((opt) => opt.id === value);

  const statusColor = {
    error: "border-danger-medium text-danger-medium",
    success: "border-success-medium text-success-medium",
    none: "border-neutral-medium text-neutral-dark",
  };

  return (
    <div className="flex w-full flex-col gap-1">
      <Popover onOpenChange={setOpen} open={open}>
        <PopoverTrigger asChild>
          <button
            aria-expanded={open}
            className={cn(
              "flex w-full items-center justify-between rounded-none border bg-background p-3 font-paragraph text-sm",
              "hover:border-neutral-dark hover:bg-neutral-lightest focus-visible:border-neutral-dark focus-visible:ring-2 focus-visible:ring-brand-medium",
              statusColor[status],
              disabled &&
                "cursor-not-allowed bg-neutral-medium text-neutral-dark opacity-50"
            )}
            disabled={disabled}
            role="combobox"
            title="Search and select a product"
            type="button"
          >
            <span className="w-full truncate text-left">
              {selectedOption ? (
                selectedOption.label
              ) : (
                <span className="text-muted-foreground">{placeholder}</span>
              )}
            </span>
            <ChevronDown
              aria-hidden
              className={cn("shrink-0", statusColor[status])}
            />
          </button>
        </PopoverTrigger>

        <PopoverContent className="z-50 w-[--radix-popover-trigger-width] p-0">
          <Command shouldFilter={true}>
            <div className="sticky top-0 z-10 bg-white px-1 py-2">
              <CommandInput autoFocus placeholder="Search products..." />
            </div>
            <CommandList>
              <CommandEmpty>No products found.</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    className="flex cursor-pointer items-center gap-2"
                    key={option.id}
                    onSelect={() => {
                      onChange(option.id);
                      setOpen(false);
                    }}
                    value={option.label}
                  >
                    <TickIcon
                      className={cn(
                        "h-4 w-4",
                        option.id === value
                          ? "text-brand-medium opacity-100"
                          : "opacity-0"
                      )}
                    />
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {status && (
        <div>
          {status === "error" && message && (
            <span className="flex items-center gap-1">
              <CloseIcon className="h-3 w-3 text-error" size="sm" />
              <Body className="text-error" size="xs">
                {message}
              </Body>
            </span>
          )}
          {status === "success" && message && (
            <span className="flex items-center gap-1">
              <TickIcon className="h-3 w-3 text-success-medium" size="sm" />
              <Body className="text-success-medium" size="xs">
                {message}
              </Body>
            </span>
          )}
          {!["error", "success"].includes(status) && message && (
            <Body className="text-neutral-medium" size="xs">
              {message}
            </Body>
          )}
        </div>
      )}
    </div>
  );
}
